import React, {useReducer} from "react";
import {Modal} from "antd";
import {produce} from "immer";
import {PassengerEditor} from "./index";
import {ModalProps} from "antd/lib/modal";

type State = {
    visible: boolean,
    paxId: string | null
}

const ActionTypes = {
    OPEN: 'OPEN' as const,
    CLOSE: 'CLOSE' as const
} as const;

type Action = {
    type: typeof ActionTypes.OPEN,
    payload: {
        paxId: string
    }
} | {
    type: typeof ActionTypes.CLOSE
}

const Reducer: React.Reducer<State, Action> = (state, action) => produce(state, (draft) => {
    switch (action.type) {
        case ActionTypes.OPEN:
            draft.visible = true;
            draft.paxId = action.payload.paxId;
            break;
        case ActionTypes.CLOSE:
            draft.visible = false;
            break;
    }
})

function useModalState(){
    const [ state, dispatch ] = useReducer(
        Reducer,
        {
            visible: false,
            paxId: null
        }
    );

    return {
        state,
        open(paxId: string){
            dispatch({ type: ActionTypes.OPEN, payload: { paxId } });
        },
        close(){
            dispatch({ type: ActionTypes.CLOSE });
        }
    }
}

export type PassengerEditorModalProps = {
    api: ReturnType<typeof useModalState>,
    edit?: boolean,
    title?: ModalProps['title'],
    width?: ModalProps['width'],
    editor?: {
        style?: React.CSSProperties,
        loadingStyle?: React.CSSProperties
    },
    onPassengerUpdated?: () => void,
    onPassengerDeleted?: () => void,
    footer?: ModalProps["footer"]
}

export const PassengerEditorModal: React.FC<PassengerEditorModalProps> & { useState: typeof useModalState } = (props) => {

    const { paxId, visible } = props.api.state;

    function renderPaxEditor(){
        if (!props.api.state.paxId) return null;

        return (
            <PassengerEditor
                paxId={paxId}
                edit={props.edit}
                onCancel={props.api.close}
                style={props.editor?.style || {
                    overflow: 'auto'
                }}
                loadingStyle={props.editor?.loadingStyle || {
                    height: '30rem',
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
                onPassengerUpdated={props.onPassengerUpdated}
                onPassengerDeleted={() => {
                    props.api.close();
                    if (typeof props.onPassengerDeleted === 'function'){
                        props.onPassengerDeleted();
                    }
                }}
            />
        )
    }

    return (
        <Modal
            visible={visible}
            title={props.title || "Edit Passenger"}
            width={props.width || "50rem"}
            onCancel={props.api.close}
            footer={props.footer || false}
            destroyOnClose
        >
            {renderPaxEditor()}
        </Modal>
    )
}

PassengerEditorModal.useState = useModalState;